import { useState, useEffect, useCallback, useRef } from 'react';
import consumer from '@/lib/cable';
import { useChatStore, type AgentType } from '@/lib/chat-store';
import { useFileStore } from '@/lib/file-store';
import { useWebContainerStore } from '@/lib/webcontainer-store';

type AiAssistantHookParams = {
  themeBuildId?: string;
  siteSlug?: string;
  enabled?: boolean;
};

type FileUpdate = {
  file: {
    id: number;
    path: string;
    content: string;
    file_type: string;
  }
};

export function useAiCodeAssistant({ themeBuildId, siteSlug, enabled = true }: AiAssistantHookParams) {
  const [isConnected, setIsConnected] = useState(false);
  const channelRef = useRef<any>(null);

  const { addMessage, updateMessage, addAgentAction, setLoading, isLoading } = useChatStore();
  const { addFile, updateFileContent, deleteFile } = useFileStore();
  const { instance: webcontainer, triggerRefresh } = useWebContainerStore();
  const currentMessageIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || (!themeBuildId && !siteSlug)) return;

    console.log('Subscribing to AI Code Assistant channel', { themeBuildId, siteSlug });

    // Create the subscription
    const channel = consumer.subscriptions.create(
      {
        channel: 'AiCodeAssistantChannel',
        ...(themeBuildId ? { theme_build_id: themeBuildId } : {}),
        ...(siteSlug ? { site_slug: siteSlug } : {})
      },
      {
        connected() {
          console.log('Connected to AI Code Assistant channel');
          setIsConnected(true);
        },

        rejected() {
          console.warn('AI Code Assistant channel subscription rejected');
          setIsConnected(false);
        },

        disconnected() {
          console.log('Disconnected from AI Code Assistant channel');
          setIsConnected(false);
        },

        received(data: any) {
          console.log('Received data:', data);

          switch (data.type) {
            case 'status':
              handleStatusUpdate(data);
              break;
            case 'message':
              handleMessageUpdate(data);
              break;
            case 'tool_call':
              handleToolCall(data);
              break;
            case 'file_update':
              handleFileUpdate(data);
              break;
            case 'error':
              handleError(data);
              break;
          }
        }
      }
    );

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
    };
  }, [themeBuildId, siteSlug, enabled]);

  useEffect(() => {
    currentMessageIdRef.current = null;
    setLoading(false);
  }, [themeBuildId, setLoading]);

  const handleStatusUpdate = (data: any) => {
    if (data.status === 'processing') {
      setLoading(true);

      // Add AI message placeholder if not exists
      if (!currentMessageIdRef.current) {
        const messageId = addMessage({
          role: 'assistant',
          content: '',
          isStreaming: true,
        });
        currentMessageIdRef.current = messageId;
      }
    } else if (data.status === 'completed') {
      setLoading(false);
      if (currentMessageIdRef.current) {
        updateMessage(currentMessageIdRef.current, { isStreaming: false });
        currentMessageIdRef.current = null;
      }
    }
  };

  const handleMessageUpdate = (data: any) => {
    if (currentMessageIdRef.current) {
      updateMessage(currentMessageIdRef.current, {
        content: data.content,
        isStreaming: false
      });
    } else {
      // If no current message, create one
      const messageId = addMessage({
        role: 'assistant',
        content: data.content,
      });
      currentMessageIdRef.current = messageId;
    }

  };

  const handleToolCall = async (data: any) => {
    console.log('🔧 Tool call received:', data.function, data.arguments?.path);
    
    // Handle file deletions with WebContainer sync
    if (data.result?.success && webcontainer) {
      const path = data.arguments?.path;
      
      if (data.function === 'delete_file' && path) {
        console.log('🗑️ Deleting file:', path);
        
        // Delete from file store (triggers reactive sync after debounce)
        deleteFile(path);
        console.log('📝 Removed file from store:', path);
        
        // Delete from WebContainer IMMEDIATELY for live preview
        try {
          await webcontainer.fs.rm(path, { force: true });
          console.log('✅ Deleted file from WebContainer:', path);
        } catch (error) {
          console.error('❌ Failed to delete file from WebContainer:', path, error);
        }
      }
    }

    if (currentMessageIdRef.current) {
      // Add agent action in the same format as database messages
      // This ensures consistent rendering whether from WebSocket or database
      addAgentAction(currentMessageIdRef.current, {
        tool_call_id: data.tool_call_id || `call_${Date.now()}`,
        function_name: data.function,
        arguments: data.arguments || {},
        result: data.result || {}
      } as any);
    }
  };

  const handleFileOperation = (data: any) => {
    console.log('📄 File operation:', data.operation, data.path);
    console.log('   Title:', data.title);
    console.log('   Description:', data.description);
    console.log('   Details:', data.details);
    
    // Add a system message showing the file operation with details
    if (currentMessageIdRef.current) {
      // Add detailed file operation info to the current message
      addAgentAction(currentMessageIdRef.current, {
        agent: 'coder',
        type: 'complete',
        message: `${data.title}\n${data.description}\n${data.details}`,
        fileChanges: [{
          path: data.path,
          action: data.operation === 'create_file' ? 'create' :
            data.operation === 'edit_file' ? 'modify' : 'delete'
        }]
      });
    } else {
      // Create a new system message for the file operation
      addMessage({
        role: 'system',
        content: `**${data.title}**\n${data.description}\n\`${data.details}\``
      });
    }
  };

  const handleFileUpdate = async (data: FileUpdate) => {
    const file = data.file;

    console.log('🔄 Received file update from AI:', file.path);

    // Check if file exists
    const existingFile = useFileStore.getState().getFileContent(file.path);

    // Update file store (triggers reactive sync after debounce)
    if (existingFile !== undefined) {
      updateFileContent(file.path, file.content);
      console.log('📝 Updated existing file in store:', file.path);
    } else {
      addFile(file.path, file.content);
      console.log('➕ Added new file to store:', file.path);
    }

    // Sync to WebContainer IMMEDIATELY for live preview
    if (webcontainer) {
      try {
        // Ensure parent directories exist
        const pathParts = file.path.split('/');
        if (pathParts.length > 1) {
          const dirPath = pathParts.slice(0, -1).join('/');
          await webcontainer.fs.mkdir(dirPath, { recursive: true });
        }
        await webcontainer.fs.writeFile(file.path, file.content);
        console.log('✅ Synced file to WebContainer for live preview:', file.path);
        
        // Trigger preview refresh after a short delay to allow WebContainer to process
        setTimeout(() => {
          console.log('🔄 Triggering preview refresh...');
          triggerRefresh();
        }, 300);
      } catch (error) {
        console.error('❌ Failed to sync file to WebContainer:', file.path, error);
      }
    }
  };

  const handleError = (data: any) => {
    const messageId = addMessage({
      role: 'system',
      content: `Error: ${data.message}`,
    });

    setLoading(false);
    currentMessageIdRef.current = null;
  };

  const sendMessage = useCallback(async (prompt: string, options?: { imageSignedId?: string; imagePreviewUrl?: string }) => {
    if (!channelRef.current || !prompt.trim()) return;

    // Add user message to UI
    addMessage({
      role: 'user',
      content: prompt,
      imageUrl: options?.imagePreviewUrl
    });

    const conversationHistory = useChatStore
      .getState()
      .messages.filter((message) => message.role === 'user' || message.role === 'assistant')
      .map((message) => ({
        role: message.role,
        content: message.content,
      }));

    // Send to server via Action Cable
    channelRef.current.send({
      prompt: prompt,
      conversation_history: conversationHistory,
      image_signed_id: options?.imageSignedId
    });

    setLoading(true);
  }, [addMessage, setLoading]);

  const clearMessages = useCallback(() => {
    currentMessageIdRef.current = null;
    setLoading(false);
  }, [setLoading]);

  return {
    isConnected,
    isLoading,
    sendMessage,
    clearMessages
  };
}
